import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { ListadoCargando, ListadoConError } from '../../../nucleo/listados/EstadosDeListado.jsx';
import { ESTADO_DE_LISTADO, useConsultaDeListado } from '../../../nucleo/listados/useConsultaDeListado.js';
import { consultarMovimientos, TIPOS } from '../../movimientos/api/movimientosApi.js';
import { consultarPanel, ESTADOS } from '../api/vencimientosApi.js';
import { BloqueDeCorteEnCurso } from '../componentes/BloqueDeCorteEnCurso.jsx';
import { CifrasQueSiSeSaben } from '../../deuda-descompuesta/componentes/CifrasQueSiSeSaben.jsx';
import { ContadorDeMensualidad } from '../../deuda-descompuesta/componentes/ContadorDeMensualidad.jsx';
import { DesgloseDelPagoEsperado } from '../../deuda-descompuesta/componentes/DesgloseDelPagoEsperado.jsx';
import {
  claseDeEstado, fecha as formatearFecha, iconoDeEstado, importe as formatearEnDivisa,
  nombreDeCuentaDeOrigen, nombreDeTarjeta,
} from '../formato.js';

/** Página única de 50: la ventana de un ciclo cabe de sobra (RN-298). */
const TAMANO_DE_PAGINA = 50;

/** Los tres estados propios del bloque de pagos (RN-297). */
const BLOQUE = Object.freeze({
  CARGANDO: 'CARGANDO', CON_DATOS: 'CON_DATOS', ERROR: 'ERROR', NO_APLICA: 'NO_APLICA',
});

/**
 * DETALLE DEL VENCIMIENTO DE UNA TARJETA (RF-113 a RF-118, RF-121, RF-123 a RF-126).
 *
 * DOS LECTURAS, Y LA SEGUNDA ESTÁ SUBORDINADA A LA PRIMERA (D-194, RNF-064):
 *   1. El panel (§1) entrega el estado, lo exigible, la cobertura, las
 *      mensualidades y el corte en curso. ES LA QUE MANDA.
 *   2. `ConsultarMovimientos` del 3/6 (§8) entrega los pagos renglón por renglón
 *      para pintar la tabla. NO MANDA, NO BLOQUEA Y NO CALCULA:
 *        · ninguna cifra del panel se recalcula con ella (RN-296);
 *        · el resto del Detalle se pinta sin esperarla y su fallo degrada SOLO su
 *          bloque (RN-297);
 *        · la pantalla no compone la ventana de fechas —se la da el servidor
 *          (RN-295, RN-308)—, no ordena, no suma y no deduce estado.
 */
export default function DetalleDelVencimiento({ base = '/cuentas/vencimientos' }) {
  const { idCuenta } = useParams();
  const hoy = obtenerFechaDeNegocioHoy();

  const consultar = useCallback(
    () => consultarPanel({ fechaLocal: hoy, idCuenta, incluirSinSaldo: true }),
    [hoy, idCuenta],
  );

  const listado = useConsultaDeListado(consultar, (r) => (r?.tarjetas ?? []).length === 0);
  const tarjeta = listado.datos?.tarjetas?.[0] ?? null;

  return (
    <Shell titulo="Detalle del vencimiento" volverA={base}>
      {listado.estado === ESTADO_DE_LISTADO.ERROR && (
        <ListadoConError error={listado.error} alReintentar={listado.recargar} />
      )}

      {listado.estado === ESTADO_DE_LISTADO.CARGANDO && (
        <ListadoCargando texto="Consultando el vencimiento…" />
      )}

      {listado.estado === ESTADO_DE_LISTADO.VACIO && (
        <div className="card" data-prueba="tarjeta-no-disponible">
          <b>Esa tarjeta ya no está en el panel</b>
          <span className="ayuda">
            Puede haberse desactivado: una tarjeta desactivada no aparece en vencimientos.
          </span>
          <Link className="btn ancho" to={base}>Volver al panel</Link>
        </div>
      )}

      {tarjeta && <Contenido tarjeta={tarjeta} base={base} />}
    </Shell>
  );
}

function Contenido({ tarjeta, base }) {
  const clase = claseDeEstado(tarjeta.claveEstado);
  const sinEstado = tarjeta.claveEstado === null || tarjeta.claveEstado === undefined;
  const sePuedePagar = tarjeta.claveEstado === ESTADOS.vencida || tarjeta.claveEstado === ESTADOS.porVencer;
  const yaNoSeEspera = tarjeta.claveEstado === ESTADOS.pagada || tarjeta.claveEstado === ESTADOS.sinSaldo;

  return (
    <>
      <div className="card">
        <div className="fila">
          <span className="crece">
            <b data-prueba="nombre-de-tarjeta">{nombreDeTarjeta(tarjeta)}</b>
            <small>
              de {tarjeta.nombrePropietario} · {tarjeta.codigoISO} · las dos personas ven y pueden
              pagar esta tarjeta
            </small>
          </span>
          <span className={`chip ${clase}`} data-prueba="estado-de-tarjeta">
            <span aria-hidden="true">{iconoDeEstado(tarjeta.claveEstado)}</span>{' '}
            {tarjeta.nombreEstado ?? 'Sin estado'}
          </span>
        </div>
      </div>

      {/* ===== BLOQUE 1 · LO EXIGIBLE ===================================== */}
      <h2 className="grupo">1 · Lo exigible</h2>

      {sinEstado ? (
        <div className="aviso e" role="status" data-prueba="renglon-sin-estado">
          ⚠ {tarjeta.advertencia}
          {/* [NUEVO · incremento 18/21 · RN-702, D-396] Misma pieza que el panel:
              las dos pantallas enseñan las mismas cifras ciertas del renglón
              degradado, o ninguna. */}
          <CifrasQueSiSeSaben tarjeta={tarjeta} datoDePrueba="cifras-que-si-se-saben-detalle" />
        </div>
      ) : (
        <div className={`card exigible ${clase}`} data-prueba="bloque-exigible">
          {/* [CAMBIADO · incremento 18/21 · RF-250] La cifra protagonista del
              periodo es lo que se espera pagar, no la deuda acumulada. */}
          <div className="exigible-rot">
            {yaNoSeEspera ? 'Se esperaba pagar este periodo' : 'Se espera pagar este periodo'} · periodo
            que cortó el {formatearFecha(tarjeta.fechaCorte)}
          </div>
          <div className="exigible-imp" data-prueba="importe-pendiente">
            {formatearEnDivisa(tarjeta.importePendiente, tarjeta.codigoISO)}
          </div>
          <div className="exigible-txt" data-prueba="texto-de-estado">
            <span aria-hidden="true">{iconoDeEstado(tarjeta.claveEstado)}</span> {tarjeta.textoEstado}
          </div>

          {/* DE DÓNDE SALE LA CIFRA DEL PERIODO (RF-252). Misma pieza compartida
              que el renglón del panel: las dos pantallas no pueden enseñar
              desgloses distintos de la misma cifra. */}
          <DesgloseDelPagoEsperado
            tarjeta={tarjeta}
            enPasado={yaNoSeEspera}
            datoDePrueba="desglose-del-pago-esperado-detalle"
          />

          {/* La resta la hace el SERVIDOR; aquí solo se muestran sus tres términos
              (RNF-059). La pantalla no resta importes. */}
          {tarjeta.claveEstado !== ESTADOS.sinSaldo && (
            <table className="tabla" data-prueba="resta-de-cobertura">
              <tbody>
                <tr>
                  {/* [CAMBIADO · incremento 18/21 · RN-695] La resta de la
                      cobertura parte del PAGO ESPERADO, que es la referencia del
                      estado (D-380). El saldo al corte sigue a la vista arriba,
                      dentro del desglose. */}
                  <td>Se esperaba pagar este periodo</td>
                  <td><b>{formatearEnDivisa(tarjeta.importePagoEsperado, tarjeta.codigoISO)}</b></td>
                </tr>
                <tr>
                  <td>− Cubierto por tus pagos</td>
                  <td><b>{formatearEnDivisa(tarjeta.importeCubierto, tarjeta.codigoISO)}</b></td>
                </tr>
                <tr>
                  <td><b>= Pendiente</b></td>
                  <td><b>{formatearEnDivisa(tarjeta.importePendiente, tarjeta.codigoISO)}</b></td>
                </tr>
              </tbody>
            </table>
          )}

          {/* [BARRIDO DE LENGUAJE · vuelta de correccion de la RONDA[1/2] ·
              RF-222, RN-626, RN-627] Cayó la frase «Las tres cifras salen del
              módulo de tarjetas tal cual.»: nombraba una pieza interna del
              proyecto y le explicaba a quien usa la aplicación de dónde saca el
              dato el sistema. Las tres fechas, que es lo útil, se quedan. */}
          <span className="ayuda">
            Corte <b>{formatearFecha(tarjeta.fechaCorte)}</b> · límite{' '}
            <b>{formatearFecha(tarjeta.fechaLimitePago)}</b> · <b>{tarjeta.diasDeGracia}</b> días de
            gracia.
          </span>
        </div>
      )}

      {/* --- Tabla de pagos del periodo (RF-125, §8) ---------------------- */}
      <TablaDePagosDelPeriodo tarjeta={tarjeta} />

      {/* --- Mensualidades del periodo (RF-118) --------------------------- */}
      <h2 className="grupo">Mensualidades a meses de este periodo</h2>
      <div className="card">
        {tarjeta.mensualidadesDelPeriodo?.length > 0 ? (
          <>
            <table className="tabla" data-prueba="mensualidades-del-periodo">
              <thead>
                <tr><th>Compra</th><th>Mes</th><th>Importe</th></tr>
              </thead>
              <tbody>
                {tarjeta.mensualidadesDelPeriodo.map((m) => (
                  <tr key={m.idMensualidad}>
                    <td>{m.descripcionDelGasto}</td>
                    {/* RNF-132 / RN-707 · misma pieza compartida del contador. */}
                    <td><ContadorDeMensualidad contador={m.contadorDeMensualidad} /></td>
                    <td><b>{formatearEnDivisa(m.importeImputadoAlMes, tarjeta.codigoISO)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="fila">
              <span className="crece">Total de mensualidades del periodo</span>
              <b>{formatearEnDivisa(tarjeta.totalMensualidadesDelPeriodo, tarjeta.codigoISO)}</b>
            </div>
            {/* EL SALDO AL CORTE NO CAMBIA: es desglose de lo que ya está contado
                (RN-268). Si cambiara al mostrar u ocultar esto, sería defecto.

                [BARRIDO DE LENGUAJE · RN-626] El «(no cambia)» que iba en pantalla
                defendía esa invariante del proyecto, no le decía nada a quien usa
                la aplicación. Se sustituye por la aclaración que sí le sirve, con
                el literal del prototipo v40 (RN-629). */}
            <div className="fila">
              <span className="crece">
                <b>Deuda acumulada al corte</b>
                <small>incluye lo que no pagaste de periodos anteriores</small>
              </span>
              <b>{formatearEnDivisa(tarjeta.importeExigible, tarjeta.codigoISO)}</b>
            </div>
            <span className="ayuda">{tarjeta.textoDesgloseDeMeses}</span>
          </>
        ) : (
          <span className="ayuda" data-prueba="sin-mensualidades">
            {/* [AMPLIADO · incremento 18/21 · RN-700] Si declaró compras a meses y
                todavía no registra sus planes, aquí se dice: es el estado real
                entre esta entrega y la siguiente. El texto de esa segunda frase lo
                arma el SERVIDOR (RNF-130).

                [BARRIDO DE LENGUAJE · RN-626, RN-627, RN-703] La frase anterior
                decía «no tiene compras a meses con parcialidad en este periodo. No
                es un error ni un dato que falte»: «parcialidad» y «dato» son
                palabras de quien construye el sistema. */}
            Todavía no hay compras a meses que caigan en este periodo.
            {tarjeta.textoAvisoDeDiferidoDeclarado && ` ${tarjeta.textoAvisoDeDiferidoDeclarado}`}
          </span>
        )}
      </div>

      {/* [RE-REDACTADO Y MOVIDO · incremento 18/21 · RN-704, D-386] Fuera del
          bloque de mensualidades, porque ahora también aplica con diferido
          declarado y sin ninguna mensualidad en el periodo.

          [CORREGIDO · vuelta de correccion de la RONDA[1/2], hallazgo MAYOR]
          EL TEXTO LO ARMA EL SERVIDOR, como los otros tres de este incremento
          (RNF-130, RNF-059): antes estaba escrito a mano aquí Y en el renglón del
          panel, dos copias del mismo párrafo que se separan en cuanto alguien
          corrige una sola. La CONDICIÓN la sigue decidiendo el servidor: el campo
          viaja no nulo exactamente cuando `avisoDivergenciaConBanco` es verdadero
          (contratos §1, aditivo), así que lo que se ve no cambia. */}
      {tarjeta.textoAvisoDeDivergenciaConBanco && (
        <div className="aviso w" role="status" data-prueba="aviso-divergencia">
          {tarjeta.textoAvisoDeDivergenciaConBanco}
        </div>
      )}

      {/* RN-706 / D-391 · Derivado al consultar, nunca persistido. */}
      {tarjeta.textoAvisoDeDiferidoFueraDeRango && (
        <div className="aviso w" role="status" data-prueba="aviso-diferido-fuera-de-rango">
          {tarjeta.textoAvisoDeDiferidoFueraDeRango}
        </div>
      )}

      {/* ===== BLOQUE 2 · CORTE EN CURSO ================================== */}
      <h2 className="grupo">2 · Corte en curso</h2>
      <BloqueDeCorteEnCurso corte={tarjeta.corteEnCurso} codigoISO={tarjeta.codigoISO} conDesglose />

      <div className="fila acciones">
        {sePuedePagar && (
          // [NUEVO · incremento 19/21 · PUNTO 2 · D-414, RF-262, RN-726a] El
          // pendiente del pago esperado viaja como DATO DE LA APERTURA y la
          // pantalla de traspaso lo precarga, editable (RNF-133). Solo esta
          // apertura queda descongelada.
          <Link
            className="btn"
            data-prueba="registrar-pago"
            to={`/movimientos/traspaso?idCuentaDestino=${tarjeta.idCuenta}`
              + `&importe=${tarjeta.importePendiente}`
              + `&importeSugerido=${tarjeta.importePendiente}`}
          >
            Registrar pago de tarjeta
          </Link>
        )}
        <Link className="btn sec" to={`/cuentas/${tarjeta.idCuenta}`}>Ver la tarjeta</Link>
        <Link className="btn sec" to={base}>Volver al panel</Link>
      </div>
    </>
  );
}

/**
 * LOS PAGOS DEL PERIODO, RENGLÓN POR RENGLÓN (RF-125, contratos §8).
 *
 * LOS FILTROS SON ESTOS Y NO OTROS (RN-293): `idCuenta`, `tipo=TRASPASO`, la ventana
 * que el panel entregó ya resuelta, `incluirCancelados=false` explícito y una página
 * de 50. NO se envían `idPeriodo`, `idUsuarioPropietario`, `texto`, `idCategoria`,
 * `idMoneda` ni `renglonesRepartidos`: enviarlos cambiaría el conjunto sin que nadie
 * lo pidiera.
 *
 * DE LO QUE LLEGA SE PINTAN SOLO LOS PAGOS **A** LA TARJETA (RN-294): el filtro
 * `idCuenta` del 3/6 devuelve el traspaso por cualquiera de sus dos extremos, así
 * que también vienen las DISPOSICIONES DE EFECTIVO, que no son pagos. Se conservan
 * los renglones cuyo `idCuentaDestino` es la tarjeta, que es literalmente la
 * definición de pago de tarjeta. Es una selección por un campo estable que el
 * servidor ya entrega, no un cálculo.
 */
function TablaDePagosDelPeriodo({ tarjeta }) {
  const [estado, setEstado] = useState(BLOQUE.CARGANDO);
  const [pagos, setPagos] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [intento, setIntento] = useState(0);

  const { idCuenta, fechaInicioVentanaDePagos, fechaFinVentanaDePagos } = tarjeta;
  const hayVentana = Boolean(fechaInicioVentanaDePagos && fechaFinVentanaDePagos);

  useEffect(() => {
    // SIN LAS DOS FECHAS NO SE LLAMA (RN-306, RN-308): jamás se llama con una
    // ventana adivinada ni compuesta por la pantalla.
    if (!hayVentana) {
      setEstado(BLOQUE.NO_APLICA);
      return undefined;
    }

    let vigente = true;
    setEstado(BLOQUE.CARGANDO);

    consultarMovimientos({
      idCuenta,
      tipo: TIPOS.traspaso,
      // COPIADAS TAL CUAL del DTO del panel: sin sumar ni restar días, sin
      // convertirlas a fecha con hora y sin recortarlas contra la fecha local.
      desde: fechaInicioVentanaDePagos,
      hasta: fechaFinVentanaDePagos,
      incluirCancelados: false,
      pagina: 1,
      tamanoPagina: TAMANO_DE_PAGINA,
    })
      .then((respuesta) => {
        if (!vigente) return;
        setPagos((respuesta?.movimientos ?? [])
          .filter((m) => String(m.idCuentaDestino) === String(idCuenta)));
        setTotalRegistros(respuesta?.totalRegistros ?? 0);
        setEstado(BLOQUE.CON_DATOS);
      })
      .catch(() => { if (vigente) setEstado(BLOQUE.ERROR); });

    return () => { vigente = false; };
  }, [idCuenta, fechaInicioVentanaDePagos, fechaFinVentanaDePagos, hayVentana, intento]);

  return (
    <>
      <h2 className="grupo">Pagos aplicados a este periodo</h2>
      <div className="card" data-prueba="bloque-de-pagos">
        {/* [BARRIDO DE LENGUAJE · RN-627, RN-703] «Ventana de imputación» es
            vocabulario de quien construye; se dice lo mismo con las palabras de
            quien paga la tarjeta. Las dos fechas y la suma de varios pagos, que
            son lo útil, se quedan. */}
        {hayVentana && (
          <span className="ayuda">
            Cuentan los pagos que hiciste entre el{' '}
            <b>{formatearFecha(fechaInicioVentanaDePagos)}</b> y el{' '}
            <b>{formatearFecha(fechaFinVentanaDePagos)}</b>. Si hiciste varios, se suman.
          </span>
        )}

        {/* CARGANDO dentro del bloque, sin cubrir la pantalla y sin bloquear ninguna
            acción: nunca hay spinner de página completa (RN-297). */}
        {estado === BLOQUE.CARGANDO && (
          <div className="ayuda" role="status" data-prueba="pagos-cargando">Cargando los pagos del periodo…</div>
        )}

        {/* FALLÓ O NO RESPONDIÓ: jamás se muestra «no hay pagos» cuando lo que
            ocurrió es que no se pudo saber (RN-297). Nulo, no cero. */}
        {(estado === BLOQUE.ERROR || estado === BLOQUE.NO_APLICA) && (
          <div className="aviso e" role="status" data-prueba="pagos-con-error">
            No se pudo cargar el detalle de los pagos.
            {/* [BARRIDO DE LENGUAJE · RN-626] La frase anterior hablaba de «esta
                pantalla» y de que la cifra «ya se calculó en el servidor»: le
                explicaba el reparto interno del sistema. Lo que la persona
                necesita saber es que la cifra de arriba sigue siendo buena y que
                puede pagar igual. */}
            <span className="ayuda">
              Lo que debes de este periodo sigue siendo correcto y puedes pagarlo. Lo único que falta
              es la lista de tus pagos.
            </span>
            {estado === BLOQUE.ERROR && (
              <button className="btn sec" type="button" data-prueba="pagos-reintentar"
                onClick={() => setIntento((n) => n + 1)}>
                Reintentar
              </button>
            )}
          </div>
        )}

        {estado === BLOQUE.CON_DATOS && pagos.length === 0 && tarjeta.cantidadDePagosDelPeriodo === 0 && (
          <span className="ayuda" data-prueba="pagos-vacio">
            Aún no hay pagos registrados para este periodo.
          </span>
        )}

        {estado === BLOQUE.CON_DATOS && pagos.length > 0 && (
          <table className="tabla" data-prueba="tabla-de-pagos">
            <thead>
              <tr><th>Fecha</th><th>Cuenta de origen</th><th>Importe</th></tr>
            </thead>
            <tbody>
              {/* EL ORDEN ES EL QUE ENTREGA EL 3/6 —fecha descendente y, a igual
                  fecha, identificador descendente—: sin `sort` propio (RN-292). */}
              {pagos.map((pago) => (
                <tr key={pago.idMovimiento} data-prueba="renglon-de-pago">
                  <td>{formatearFecha(pago.fecha)}</td>
                  {/* SOLO EL NOMBRE DE LA CUENTA, sin su dueño (D-199, RN-309). */}
                  <td data-prueba="cuenta-de-origen">{nombreDeCuentaDeOrigen(pago)}</td>
                  <td><b>{formatearEnDivisa(pago.importe, pago.codigoISO)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* SI EL AGREGADO Y EL LISTADO NO COINCIDEN, MANDA EL AGREGADO DEL PANEL
            (RN-296): no se recalcula la cobertura, no se oculta la tabla y no se
            muestran dos cifras compitiendo. */}
        {estado === BLOQUE.CON_DATOS && pagos.length !== tarjeta.cantidadDePagosDelPeriodo && (
          <div className="aviso w" role="status" data-prueba="leyenda-detalle-incompleto">
            {/* [BARRIDO DE LENGUAJE · RN-626] «Lo que se debe se calculó con…»
                ponía al sistema de sujeto; la cifra y los pagos que la respaldan,
                que es lo que la persona necesita contrastar, se conservan. */}
            Puede que falten pagos en esta lista. Lo que debes de este periodo ya cuenta{' '}
            {tarjeta.cantidadDePagosDelPeriodo}
            {tarjeta.cantidadDePagosDelPeriodo === 1 ? ' pago por ' : ' pagos por '}
            {formatearEnDivisa(tarjeta.importeCubierto, tarjeta.codigoISO)}.
          </div>
        )}

        {estado === BLOQUE.CON_DATOS && totalRegistros > TAMANO_DE_PAGINA && (
          <div className="ayuda" data-prueba="leyenda-tope">
            Se muestran los primeros {TAMANO_DE_PAGINA} pagos de este periodo.{' '}
            <Link to="/movimientos">Ver el historial completo</Link>
          </div>
        )}

        {/* [BARRIDO DE LENGUAJE · RN-626] Cayeron «y no es un olvido» —que
            defendía la pantalla— y «en la siguiente consulta», que le contaba
            cuándo vuelve a mirar el sistema. Los dos hechos de dinero se quedan,
            con el literal del prototipo v40 para el primero (RN-629). */}
        <span className="ayuda">
          Solo cuentan los pagos hechos <b>después</b> del corte: uno anterior ya viene descontado
          dentro de la deuda al corte. Si cancelas un pago que cubría el periodo, esta tarjeta vuelve
          a aparecer con lo que falte.
        </span>
      </div>
    </>
  );
}
